'use client';

import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useMutation } from '@parkchirp/api-client';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import React, { useCallback, useEffect, useState } from 'react';

import { CREATE_CONTRACT_DOCUMENT, UPDATE_CONTRACT_DOCUMENT } from '../../../mutations';
import { ContractDocumentsFormData, ContractDocumentsModalProps } from '../../../types';
import { formatEnumValueForDisplay, uploadDocToS3 } from '../../../utils';
import { EditorModal } from '../../modals';
import {
	contractDocsInitialState,
	contractType,
	getFieldsToEdit,
	getMissingFields,
	subscriberType,
} from './contractDocumentsConfig';
import { UploadContractDocument } from './uploadContractDocument';

export const ContractDocumentsModal = ({
	refetch,
	editRow,
	handleClose,
	getSignedUrl,
	setShowSnackbar,
	theme,
}: ContractDocumentsModalProps) => {
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [subName, setSubName] = useState('');
	const [file, setFile] = useState<File | null>(null);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [formData, setFormData] = useState<ContractDocumentsFormData>(contractDocsInitialState);
	const [createDocument, { loading: createLoading }] = useMutation(CREATE_CONTRACT_DOCUMENT);
	const [updateDocument, { loading: updateLoading }] = useMutation(UPDATE_CONTRACT_DOCUMENT);

	useEffect(() => {
		if (editRow) {
			setSubName(editRow.subscriptionName);
			setFormData(getFieldsToEdit(editRow));
		}
	}, [editRow, setFormData]);

	const handleInputChange = useCallback(
		(e) => {
			setFormData({
				...formData,
				[e.target.name]: e.target.value,
			});
			setHasUnsavedChanges(true);
		},
		[formData, setFormData],
	);

	const validateAndSave = useCallback(async () => {
		const missingFields = getMissingFields(formData);

		if (missingFields.length) {
			setShowSnackbar({
				message: `Missing Required Field(s): ${missingFields.join(', ')}`,
				severity: 'error',
				autoHideDuration: 3000,
			});
			return;
		}

		try {
			if (file) {
				setUploadLoading(true);
				const urlResponse = await getSignedUrl({
					variables: {
						type: 'put',
						key: formData.documentPath,
					},
					context: {
						apiVersion: 'v2',
					},
				});
				if (!urlResponse?.data?.contractDocumentSignedUrl?.url) {
					throw new Error('Failed to get signed url');
				}
				const uploadResponse = await uploadDocToS3(urlResponse.data.contractDocumentSignedUrl.url, file);
				setUploadLoading(false);
				if (!uploadResponse) {
					throw new Error('Failed to upload document');
				}
			}
			if (editRow) {
				const response = await updateDocument({
					variables: getFieldsToEdit(formData),
					context: {
						apiVersion: 'v2',
					},
				});

				if (!response?.data?.updateContractDocument) {
					setShowSnackbar({
						message: 'Failed to update document',
						severity: 'error',
						autoHideDuration: 6000,
					});
					return;
				}

				setShowSnackbar({
					message: 'Successfully updated document',
					severity: 'success',
					autoHideDuration: 6000,
				});
			} else {
				const response = await createDocument({
					variables: omitBy(formData, isNil),
					context: {
						apiVersion: 'v2',
					},
				});

				if (!response?.data?.createContractDocument?.id) {
					setShowSnackbar({
						message: 'Failed to create document',
						severity: 'error',
						autoHideDuration: 6000,
					});
					return;
				}

				setShowSnackbar({
					message: 'Successfully created document',
					severity: 'success',
					autoHideDuration: 6000,
				});
			}
		} catch (err) {
			setUploadLoading(false);
			setShowSnackbar({
				message: err.message,
				severity: 'error',
				autoHideDuration: 6000,
			});
			return;
		}

		refetch();
		handleClose();
	}, [formData, setShowSnackbar, refetch, handleClose, createDocument, updateDocument, editRow, getSignedUrl, file]);
	return (
		<Box>
			<EditorModal
				handleClose={handleClose}
				handleSave={validateAndSave}
				headerText={!editRow ? 'Add New Document' : `Edit Document For Subscription: ${subName}`}
				hasUnsavedChanges={hasUnsavedChanges}
				loading={createLoading || updateLoading || uploadLoading}
				theme={theme}
			>
				<Grid
					container
					spacing={3}
					sx={{
						borderBottom: 1,
						borderColor: 'divider',
						padding: '20px 0',
					}}
				>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<TextField
								id='account-id-input'
								label='Account ID'
								type='number'
								name='accountId'
								value={formData.accountId || ''}
								onChange={handleInputChange}
								InputLabelProps={{ shrink: true }}
								disabled={!!editRow}
								autoComplete='off'
								inputProps={{ 'data-cy': 'account-id-input' }}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<InputLabel id='subscriber-type'>Subscriber Type</InputLabel>
							<Select
								label='Subscriber Type'
								labelId='subscriber-type'
								id='subscriber-type'
								name='subscriberType'
								value={formData.subscriberType || ''}
								onChange={handleInputChange}
								data-cy='subscriber-type-select-menu'
							>
								{subscriberType.map((item) => (
									<MenuItem key={item} value={item}>
										{formatEnumValueForDisplay(item)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<InputLabel id='contract-type'>Contract Type</InputLabel>
							<Select
								label='Contract Type'
								labelId='contract-type'
								id='contract-type'
								name='contractType'
								value={formData.contractType || ''}
								onChange={handleInputChange}
								data-cy='contract-type-select-menu'
							>
								{contractType.map((item) => (
									<MenuItem key={item} value={item}>
										{formatEnumValueForDisplay(item)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<TextField
								id='contract-date'
								label='Contract Date'
								name='contractDate'
								value={formData.contractDate || ''}
								onChange={handleInputChange}
								type='date'
								InputLabelProps={{ shrink: true }}
								inputProps={{ 'data-cy': 'contract-date-picker' }}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<TextField
								id='commencement-date'
								label='Commencement Date'
								name='commencementDate'
								type='date'
								value={formData.commencementDate || ''}
								onChange={handleInputChange}
								InputLabelProps={{ shrink: true }}
								inputProps={{ 'data-cy': 'commencement-date-picker' }}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<TextField
								id='expiration-date'
								label='Expiration Date'
								name='expirationDate'
								type='date'
								value={formData.expirationDate || ''}
								onChange={handleInputChange}
								InputLabelProps={{ shrink: true }}
								inputProps={{ 'data-cy': 'expiration-date-picker' }}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<TextField
								id='landlord-entity'
								label='Landlord Entity'
								name='landlordEntity'
								type='text'
								value={formData.landlordEntity || ''}
								onChange={handleInputChange}
								InputLabelProps={{ shrink: true }}
								autoComplete='off'
								inputProps={{ 'data-cy': 'landlord-entity-input' }}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<TextField
								id='subscriber-entity'
								label='Subscriber Entity'
								name='subscriberEntity'
								type='text'
								value={formData.subscriberEntity || ''}
								onChange={handleInputChange}
								InputLabelProps={{ shrink: true }}
								autoComplete='off'
								inputProps={{ 'data-cy': 'subscriber-entity-input' }}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl style={{ width: '100%' }}>
							<TextField
								id='parking-contract-section'
								label='Parking Contract Section'
								name='parkingContractSection'
								type='text'
								value={formData.parkingContractSection || ''}
								onChange={handleInputChange}
								InputLabelProps={{ shrink: true }}
								autoComplete='off'
								inputProps={{ 'data-cy': 'parking-contract-section-input' }}
							/>
						</FormControl>
					</Grid>
					<Grid item>
						<UploadContractDocument
							file={file}
							accountId={formData.accountId}
							setFile={setFile}
							setFormData={setFormData}
							setHasUnsavedChanges={setHasUnsavedChanges}
						/>
					</Grid>
				</Grid>
			</EditorModal>
		</Box>
	);
};
